import { NurseLayout } from "@/components/layout/nurse-layout";
import { useListConditions, useListMedications } from "@workspace/api-client-react";
import { Link } from "wouter";
import { Stethoscope, FlaskConical, ChevronRight, BookOpenCheck } from "lucide-react";
import { CpdModulesContent } from "@/components/cpd/cpd-modules-hub";
import { useState } from "react";

type Tab = "modules" | "resources";

export default function NurseLearn() {
  const [activeTab, setActiveTab] = useState<Tab>("modules");
  const { data: conditions } = useListConditions({ audience: "nurse" });
  const { data: medications } = useListMedications({});

  const conditionCount = conditions?.length ?? null;
  const medicationCount = medications?.length ?? null;

  const resources = [
    {
      href: "/nurse/conditions",
      icon: Stethoscope,
      iconBg: "bg-teal-100",
      iconColor: "text-teal-700",
      borderColor: "border-teal-200 hover:border-teal-400",
      badge: "Conditions Reference",
      badgeColor: "bg-teal-50 text-teal-700 border-teal-200",
      title: "Rheumatic Conditions",
      description:
        "Deep-dive clinical learning for each rheumatic disease — aetiopathogenesis, pathological mechanisms, classification criteria (ACR/EULAR), structured investigations, and evidence-based management.",
      features: [
        "Aetiopathogenesis & disease mechanisms",
        "ACR / EULAR classification criteria",
        "Investigations — bloods, imaging, biopsies",
        "Medical & nursing management",
      ],
      count: conditionCount !== null ? `${conditionCount} condition${conditionCount !== 1 ? "s" : ""}` : "Conditions",
    },
    {
      href: "/nurse/pharmacology",
      icon: FlaskConical,
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-700",
      borderColor: "border-indigo-200 hover:border-indigo-400",
      badge: "Pharmacology Reference",
      badgeColor: "bg-indigo-50 text-indigo-700 border-indigo-200",
      title: "Pharmacology & Drug Reference",
      description:
        "Comprehensive drug profiles covering pharmacokinetics, pharmacodynamics, mechanism of action, adverse effect profiles, dosage protocols, and a live drug interaction checker.",
      features: [
        "Pharmacokinetics (ADME) & pharmacodynamics",
        "Mechanism of action explanations",
        "Detailed adverse effect profiles with severity grading",
        "Live drug interaction checker (RxNorm)",
      ],
      count: medicationCount !== null ? `${medicationCount} drug${medicationCount !== 1 ? "s" : ""}` : "Drugs",
    },
  ];

  return (
    <NurseLayout title="Learning Hub">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-teal-100 p-2.5 rounded-xl">
            <BookOpenCheck className="h-6 w-6 text-teal-700" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Learning Hub</h2>
        </div>
        <p className="text-slate-600 max-w-2xl">
          Structured CPD learning modules, clinical references, and pharmacology resources for rheumatology specialist nurses — mapped to the NMC Code and BSR/NICE/EULAR guidance.
        </p>
      </div>

      <div className="flex gap-1 mb-6 bg-slate-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveTab("modules")}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
            activeTab === "modules" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          CPD Modules
        </button>
        <button
          onClick={() => setActiveTab("resources")}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
            activeTab === "resources" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          Clinical References
        </button>
      </div>

      {activeTab === "modules" && (
        <CpdModulesContent moduleBasePath="/nurse/learn" />
      )}

      {activeTab === "resources" && (
        <div>
          <div className="grid gap-6 md:grid-cols-2">
            {resources.map((r) => (
              <Link key={r.href} href={r.href}>
                <div className={`group bg-white rounded-2xl border-2 shadow-sm hover:shadow-md transition-all cursor-pointer p-8 h-full flex flex-col ${r.borderColor}`}>
                  <div className="flex items-start justify-between gap-4 mb-5">
                    <div className={`p-3 rounded-xl ${r.iconBg}`}>
                      <r.icon className={`h-7 w-7 ${r.iconColor}`} />
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border mt-1 ${r.badgeColor}`}>
                      {r.count}
                    </span>
                  </div>
                  <div className="mb-1">
                    <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${r.badgeColor}`}>
                      {r.badge}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mt-3 mb-3 group-hover:text-teal-700 transition-colors">
                    {r.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5 flex-1">{r.description}</p>
                  <ul className="space-y-2 mb-6">
                    {r.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <ChevronRight className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-sm text-teal-600 font-semibold">
                    <span>Browse {r.title}</span>
                    <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 bg-teal-900 rounded-2xl p-8 text-white">
            <h3 className="text-lg font-bold mb-2">NMC Code &amp; CPD</h3>
            <p className="text-teal-200 text-sm leading-relaxed max-w-xl">
              All content is mapped to NMC Code standards and BSR / NICE / EULAR guidance. Use these resources for CPD reflection, knowledge consolidation before clinic, and to support patient education. Always apply clinical judgement and local protocols.
            </p>
          </div>
        </div>
      )}
    </NurseLayout>
  );
}
